import { useEffect, useRef, type RefObject } from "react";

import bg2001 from "../assets/textures/bg-2001.webp";
import bgAmadeus from "../assets/textures/bg-amadeus.webp";
import bgEmpire from "../assets/textures/bg-empire.webp";

/**
 * Full-bleed film stills used as page texture.
 *
 * These are wallpaper, not editorial images — they sit under an ink scrim so
 * the colour cast and the shape of the frame carry, and nothing else competes
 * with the type. Anything that illustrates a specific review belongs with that
 * review's record, not here.
 */
const TEXTURES = {
  /** 2001: A Space Odyssey — red HAL corridor. Warmest of the three. */
  odyssey: bg2001,
  /** Amadeus — candlelit opera house. Gold, matches the lamp accent. */
  opera: bgAmadeus,
  /** The Empire Strikes Back — Cloud City gantry. Cold blue. */
  gantry: bgEmpire,
} as const;

export type BackdropName = keyof typeof TEXTURES;

/**
 * How much of the still is allowed through.
 *
 * `veil` is for pages that are mostly small text (long rating tables); `scene`
 * is for pages with room to breathe, where the image can carry some weight.
 *
 * Both were opened up: at 95/92/97 percent ink the stills were doing nothing
 * but costing a request — the corridor and the gantry were indistinguishable
 * from flat near-black. The scrim still clears WCAG AA for body copy at these
 * values because the type sits on paper-50 against ink-900 either way; what
 * changed is how much of the image survives underneath it.
 */
type Intensity = "veil" | "scene";

const SCRIM: Record<Intensity, string> = {
  veil: "from-ink-900/90 via-ink-900/86 to-ink-900/94",
  scene: "from-ink-900/80 via-ink-900/72 to-ink-900/90",
};

/**
 * Treatment applied to the still itself.
 *
 * Two jobs, and the second one matters more than it looks. Brightness and a
 * softened contrast lift the frame out of the shadows — these are all
 * night-time stills, so thinning the scrim alone just revealed more black.
 *
 * The blur is what makes them usable as *backgrounds*. The HAL corridor and the
 * Cloud City gantry are full of hard, regular, high-contrast rectangles, and at
 * full sharpness those read as a second page competing with the type in front
 * of them — every card looked like it was floating over a photograph. Blurred,
 * the same frame reads as colour and shape, which is all a backdrop should be.
 */
const IMAGE_FILTER =
  "brightness(1.22) contrast(0.88) saturate(1.05) blur(3px)";

/**
 * The slight overscale is not decoration — a blur samples beyond the element's
 * own box, and there is nothing out there, so an unscaled blurred image feathers
 * into transparency and draws a pale halo down all four edges of the viewport.
 * Growing it a few percent keeps that fringe off screen.
 */
const IMAGE_STYLE: React.CSSProperties = {
  filter: IMAGE_FILTER,
  transform: "scale(1.06)",
};

interface PageBackdropProps {
  name: BackdropName;
  intensity?: Intensity;
}

/**
 * Fixed backdrop layer for a route.
 *
 * Rendered as a `fixed` sibling behind the content rather than as a
 * `background-attachment: fixed` on the page wrapper — that property repaints
 * the whole layer on every scroll frame and is simply ignored on iOS Safari,
 * which is what makes parallax-ish page backgrounds look broken on phones.
 *
 * Drop it as the first child of a page and give the page's own wrapper
 * `relative` so the content stacks above it.
 */
export function PageBackdrop({ name, intensity = "veil" }: PageBackdropProps) {
  return (
    <div aria-hidden="true" className="pointer-events-none fixed inset-0 -z-10">
      <img
        src={TEXTURES[name]}
        alt=""
        loading="eager"
        decoding="async"
        className="h-full w-full object-cover"
        style={IMAGE_STYLE}
      />

      <div
        className={`grain absolute inset-0 bg-gradient-to-b ${SCRIM[intensity]}`}
      />
    </div>
  );
}

interface ScrollBackdropProps {
  name: BackdropName;
  intensity?: Intensity;
  /**
   * The section the backdrop waits behind — the hero. The still fades in as
   * this element's bottom edge climbs out of the viewport.
   */
  after: RefObject<HTMLElement | null>;
}

/**
 * A backdrop that is not there until you scroll past something.
 *
 * The home page hero is its own composition — gold pool of light, film reel,
 * poster type — and a still behind it would fight all three. Below the hero
 * there is nothing but cards on flat ink, which is where the image earns its
 * place. So it fades in exactly as the hero leaves.
 *
 * Opacity is written straight to the node inside a rAF-coalesced scroll
 * handler. Driving it through React state would re-render the whole page on
 * every scroll frame to change one number that only the compositor cares about.
 */
export function ScrollBackdrop({
  name,
  intensity = "scene",
  after,
}: ScrollBackdropProps) {
  const layer = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = layer.current;
    if (!node) return;

    let queued = false;

    /**
     * The anchor's bottom edge, as a document offset.
     *
     * Cached, so the scroll handler can work from `scrollY` alone. Reading a
     * rect on every scroll frame forces a synchronous layout, and this is one
     * of three handlers that were doing it — together they were a measurable
     * part of why scrolling felt heavy.
     */
    let anchorBottom = 0;

    const remeasure = () => {
      const anchor = after.current;
      if (!anchor) return;
      anchorBottom = anchor.getBoundingClientRect().bottom + window.scrollY;
    };

    const apply = () => {
      queued = false;

      const viewport = window.innerHeight;
      const bottom = anchorBottom - window.scrollY;

      /*
       * Measured against the hero's bottom edge climbing the screen, not
       * against scroll position — a hero shorter than the viewport is already
       * partly "scrolled past" at rest.
       *
       * The start point is deliberately past the halfway mark rather than at
       * the viewport edge. The hero no longer paints its own background (the
       * reel lives on a layer behind it), so a backdrop that began fading in
       * the moment the page loaded would be visible *underneath the hero* at
       * rest, which is exactly what it is meant not to be.
       */
      const start = viewport * 0.55;
      const end = viewport * 0.1;
      const progress = (start - bottom) / Math.max(1, start - end);

      node.style.opacity = String(Math.min(1, Math.max(0, progress)));
    };

    const onScroll = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(apply);
    };

    const onResize = () => {
      remeasure();
      onScroll();
    };

    remeasure();
    apply();

    // The hero's height settles after its content and fonts land, and it is
    // the anchor — so re-measure once the layout has had a chance to finish.
    const settle = window.setTimeout(onResize, 0);

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize, { passive: true });

    return () => {
      window.clearTimeout(settle);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, [after]);

  return (
    <div
      ref={layer}
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 -z-10 opacity-0"
    >
      <img
        src={TEXTURES[name]}
        alt=""
        // Below the fold by definition, so it must not compete with the hero
        // for bandwidth on first paint.
        loading="lazy"
        decoding="async"
        className="h-full w-full object-cover"
        style={IMAGE_STYLE}
      />

      <div
        className={`grain absolute inset-0 bg-gradient-to-b ${SCRIM[intensity]}`}
      />
    </div>
  );
}
